import { type TicketRequest } from '@dfg/brahma-types';
import {
  CREATE_TICKET_API_URL,
  CREATE_TICKET_SERVER_API_URL,
  POST
} from './constants';

/**
 * Logs an API error by POSTing to the ticket-creation API.
 * Fire-and-forget: callers should use `void logAutomatedError(...)`.
 * Catches all errors silently to prevent recursive logging.
 */
export async function logAutomatedError(payload: TicketRequest): Promise<void> {
  try {
    if (process.env.NEXT_PUBLIC_ENV_NAME === 'local') return;
    const apiUrl =
      typeof window !== 'undefined'
        ? CREATE_TICKET_API_URL
        : CREATE_TICKET_SERVER_API_URL;
    const { screenshot: _screenshot, ...rest } = payload;
    await fetch(apiUrl, {
      method: POST,
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(rest)
    });
  } catch {
    // Silently swallow all exceptions to prevent recursive error logging
  }
}

/**
 * Extracts diagnostic metadata from a fetch Response for error reporting.
 * Captures API Gateway request IDs and other infrastructure headers
 * that help trace where a request failed in the infra chain.
 *
 * Usage: call this in the error handling path of network.ts to enrich
 * the automated ticket with infrastructure context.
 */
export function extractResponseDiagnostics(response: Response): string {
  try {
    const diagnostics: string[] = [];

    // AWS API Gateway request ID
    const amznRequestId = response.headers?.get('x-amzn-requestid');
    if (amznRequestId) {
      diagnostics.push(`apigw-req-id:${amznRequestId}`);
    }

    // AWS trace ID
    const amznTraceId = response.headers?.get('x-amzn-trace-id');
    if (amznTraceId) {
      diagnostics.push(`trace:${amznTraceId}`);
    }

    // CloudFront request ID
    const cfRequestId = response.headers?.get('x-amz-cf-id');
    if (cfRequestId) {
      diagnostics.push(`cf-id:${cfRequestId}`);
    }

    // Response status text
    if (response.statusText) {
      diagnostics.push(`statusText:${response.statusText}`);
    }

    return diagnostics.length > 0 ? diagnostics.join(' | ') : '';
  } catch {
    return '';
  }
}
